import { getVenueList } from '../../api/venueApi';
import { getVenueTagList } from '../../api/venueTagApi';
import { SimpleVenueData, VenueFilterData } from '../../dto/EntityRepository';
import { useExhibitionVenueStore } from '../../store/exhibitionVenueStore';
import { getDefaultVenueSortData } from '../../util/venueListParams';
import { useApiWrapper } from './apiWrapper';

const VENUE_LIST_PAGE_SIZE = 100;

let latestVenueListRequestId = 0;
let latestTagListRequestId = 0;

const normalizeVenueSummary = (venue: SimpleVenueData): SimpleVenueData => ({
  ...venue,
  id: String(venue.id),
});

const fetchAllVenues = async (filterData: VenueFilterData): Promise<SimpleVenueData[]> => {
  const sortData = getDefaultVenueSortData();
  let page = 1;
  let totalPages = 1;
  const all: SimpleVenueData[] = [];

  while (page <= totalPages) {
    const response = await getVenueList(page, VENUE_LIST_PAGE_SIZE, sortData, filterData);
    all.push(...response.data.content.map(normalizeVenueSummary));
    totalPages = Math.max(1, response.data.totalPages);
    page += 1;
  }

  return all;
};

export const useExhibitionVenue = () => {
  const store = useExhibitionVenueStore();
  const withApiHandler = useApiWrapper();

  const loadVenues = async (filterData = useExhibitionVenueStore.getState().filterData) => {
    const requestId = ++latestVenueListRequestId;

    await withApiHandler(async () => {
      const venues = await fetchAllVenues(filterData);
      if (requestId !== latestVenueListRequestId) return;
      store.setVenueList(venues);
    }, ['exhibition-venue-list', filterData], {}, false);
  };

  const loadTags = async () => {
    const requestId = ++latestTagListRequestId;
    await withApiHandler(async () => {
      try {
        const response = await getVenueTagList();
        if (requestId !== latestTagListRequestId) return;
        store.setTags(response.data);
      } catch (error) {
        if (requestId !== latestTagListRequestId) return;
        throw error;
      }
    }, ['exhibition-venue-tag-list'], {}, false);
  };

  const selectVenue = (venueId: string) => {
    store.setSelectedVenueId(venueId);
  };

  const setFilterData = (filterData: VenueFilterData) => {
    store.setFilterData(filterData);
    void loadVenues(filterData);
  };

  return {
    ...store,
    loadVenues,
    loadTags,
    selectVenue,
    setFilterData,
  };
};
